import { db } from '@/core/db/dexie'
import { catalogEndpoints } from '@/core/api/endpoints'
import { ApiError } from '@/core/api/fetchClient'
import type { CatMve } from '@/core/db/entities'
import { mveDtoToEntity, servicoDtoToEntity } from '@/core/db/mappers'
import { hojeSP } from './carreteiroRegras'
import { gravarCache } from './carreteiroCache'

/**
 * Pré-carga do Carreteiro para uso SEM SINAL.
 *
 * As três listas que dependem do SIGA (chefe da obra, caminhões da obra,
 * serviços do caminhão) só entravam no cache quando o operador abria aquela
 * obra/caminhão COM rede. Obra ou caminhão "novo" no campo = lista vazia.
 * Aqui, com rede, percorremos as obras do projeto selecionado e gravamos nas
 * MESMAS chaves que os hooks leem (`chefe:<obra>`, `mves:<obra>`,
 * `servicos:<mve>`).
 *
 * Economia de requests: os caminhões dependem do CHEFE do projeto na data, não
 * da obra — então buscamos a lista uma vez por chefe e replicamos para todas as
 * obras dele.
 */

const MAX_OBRAS = 80
const MAX_MVES = 200
const PARALELO = 4

export interface ResultadoCarreteiro {
  status: 'ok' | 'parcial' | 'falha'
  obras: number
  mves: number
  message?: string
}

class SemRede extends Error {}

async function emParalelo<T>(itens: T[], fn: (item: T) => Promise<void>): Promise<void> {
  const fila = [...itens]
  let parou = false
  async function trabalhador() {
    for (;;) {
      const item = fila.shift()
      if (item === undefined || parou) return
      try {
        await fn(item)
      } catch (err) {
        if (err instanceof SemRede) parou = true
      }
    }
  }
  await Promise.all(Array.from({ length: PARALELO }, trabalhador))
  if (parou) throw new SemRede()
}

function falhaDeRede(err: unknown): boolean {
  return !(err instanceof ApiError) || err.isOffline || err.status === 401 || err.status >= 500
}

export async function aquecerCarreteiro(projetoId: number, obraAtualId: number | null): Promise<ResultadoCarreteiro> {
  const dt = hojeSP()
  let obras = await db.cat_obra.where('projetoId').equals(projetoId).toArray()
  if (obras.length === 0) return { status: 'ok', obras: 0, mves: 0 }

  // Prioridade quando o projeto é enorme: obra atual, obras com rascunho/ficha recente.
  if (obras.length > MAX_OBRAS) {
    const quentes = new Set<number>()
    if (obraAtualId != null) quentes.add(obraAtualId)
    for (const d of await db.draft_carreteiro.toArray()) if (d.obraId != null) quentes.add(d.obraId)
    for (const o of await db.out_carreteiro.where('projetoId').equals(projetoId).toArray()) if (o.obraId != null) quentes.add(o.obraId)
    obras = [...obras.filter((o) => quentes.has(o.id)), ...obras.filter((o) => !quentes.has(o.id))].slice(0, MAX_OBRAS)
  }

  const obrasPorChefe = new Map<number, number[]>()
  try {
    await emParalelo(obras, async (obra) => {
      try {
        const res = await catalogEndpoints.chefeObra(obra.id, dt)
        if (!res.chefe) return
        await gravarCache(`chefe:${obra.id}`, { chefe: res.chefe })
        obrasPorChefe.set(res.chefe.id, [...(obrasPorChefe.get(res.chefe.id) ?? []), obra.id])
      } catch (err) {
        if (falhaDeRede(err)) throw new SemRede()
      }
    })

    const mves = new Map<number, CatMve>()
    for (const [, obraIds] of obrasPorChefe) {
      try {
        const res = await catalogEndpoints.mvesPorObra(obraIds[0], dt)
        const items = (res.data?.items ?? []).map(mveDtoToEntity)
        items.sort((a, b) => (a.prefixo ?? '').localeCompare(b.prefixo ?? ''))
        for (const id of obraIds) await gravarCache(`mves:${id}`, { dt, items })
        for (const m of items) mves.set(m.id, m)
      } catch (err) {
        if (falhaDeRede(err)) throw new SemRede()
      }
    }

    const alvo = [...mves.values()].slice(0, MAX_MVES)
    await emParalelo(alvo, async (m) => {
      try {
        const res = await catalogEndpoints.servicosCarreteiroPorMve(m.id, dt)
        await gravarCache(`servicos:${m.id}`, { items: (res.data?.items ?? []).map(servicoDtoToEntity) })
      } catch (err) {
        if (falhaDeRede(err)) throw new SemRede()
      }
    })
    return { status: 'ok', obras: obras.length, mves: alvo.length }
  } catch (err) {
    if (err instanceof SemRede) return { status: 'parcial', obras: obras.length, mves: 0, message: 'rede caiu durante a pré-carga' }
    return { status: 'falha', obras: obras.length, mves: 0, message: err instanceof Error ? err.message : String(err) }
  }
}
